// frontend/src/services/realtime.js

import Ably from "ably/promises";
import { getAuthToken } from "./auth";

let realtimeClientPromise = null;

// PATCH 3D: Realtime status tracking
let _realtimeStatus = 'initializing';
let _statusListeners = [];
let _disconnectedAt = null;
let _reconnectAttempts = 0;
const DEGRADED_TIMEOUT_MS = 10000; // 10 seconds disconnected = degraded
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Get current realtime connection status
 * @returns {'connected' | 'connecting' | 'disconnected' | 'degraded'}
 */
export function getRealtimeStatus() {
  return _realtimeStatus;
}

/**
 * Subscribe to realtime status changes
 * @param {Function} listener - Callback(status)
 * @returns {Function} Unsubscribe function
 */
export function onRealtimeStatusChange(listener) {
  _statusListeners.push(listener);
  // Immediately call with current status
  listener(_realtimeStatus);
  return () => {
    _statusListeners = _statusListeners.filter(l => l !== listener);
  };
}

function _setRealtimeStatus(status) {
  if (_realtimeStatus !== status) {
    console.log(`[realtime] Status changed: ${_realtimeStatus} -> ${status}`);
    _realtimeStatus = status;
    _statusListeners.forEach(l => {
      try { l(status); } catch (e) { console.error('[realtime] Status listener error:', e); }
    });
  }
}

/**
 * Noop client for disabled/no-config environments.
 */
function createNoopClient() {
  return {
    channels: {
      get: () => ({
        subscribe: () => {},
        unsubscribe: () => {},
        publish: () => Promise.resolve(),
        attach: () => Promise.resolve(),
        detach: () => Promise.resolve(),
        on: () => {},
        off: () => {},
        presence: {
          subscribe: () => {},
          unsubscribe: () => {},
          enter: () => Promise.resolve(),
          leave: () => Promise.resolve(),
          update: () => Promise.resolve(),
          get: (callback) => {
            if (callback) callback(null, []);
            return Promise.resolve([]);
          },
        },
      }),
      release: () => {},
    },
    connection: {
      on: () => {},
      off: () => {},
      once: () => Promise.resolve(),
      state: 'initialized',
    },
    auth: { clientId: null },
    close: () => {},
  };
}

/**
 * Feature flag:
 * - Enable manually: localStorage.setItem('ul_enable_realtime', '1')
 * - Or set REACT_APP_ENABLE_REALTIME=true in production
 */
const ENABLE_REALTIME =
  process.env.REACT_APP_ENABLE_REALTIME === 'true' ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('ul_enable_realtime') === '1');

// Debug-gated Ably timing metrics
const ABLY_DEBUG = typeof localStorage !== 'undefined' && localStorage.getItem('ably_debug') === '1';
const ablyMetrics = {
  clientStartTime: null,
  clientReadyTime: null,
  channelAttachTimes: {},
  reconnectCount: 0,
  detachCount: 0,
  sessionId: Date.now().toString(36),
  // User-perceived latency markers
  firstMessageEventMs: null,
  firstTypingEventMs: null,
  // Context for debugging
  currentRoute: null,
  currentMatchId: null,
};

// Update context when route changes
const updateMetricsContext = () => {
  if (typeof window !== 'undefined') {
    ablyMetrics.currentRoute = window.location.pathname;
    // Extract matchId from /chat/:id routes
    const chatMatch = window.location.pathname.match(/\/chat\/([^/]+)/);
    ablyMetrics.currentMatchId = chatMatch ? chatMatch[1] : null;
  }
};

const logAblyMetric = (event, data = {}) => {
  if (!ABLY_DEBUG) return;
  const timestamp = Date.now();
  updateMetricsContext();
  console.log(`[Ably Metrics] ${event}`, { ...data, timestamp });
  
  // Store metrics for debugging
  if (event === 'client_start') ablyMetrics.clientStartTime = timestamp;
  if (event === 'client_ready') {
    ablyMetrics.clientReadyTime = timestamp;
    const duration = ablyMetrics.clientStartTime ? timestamp - ablyMetrics.clientStartTime : null;
    console.log(`[Ably Metrics] Client ready in ${duration}ms`);
  }
  if (event === 'reconnect') ablyMetrics.reconnectCount++;
  if (event === 'detach') ablyMetrics.detachCount++;
  
  // Track first message/typing events for user-perceived latency
  if (event === 'first_message' && !ablyMetrics.firstMessageEventMs) {
    ablyMetrics.firstMessageEventMs = timestamp;
    console.log(`[Ably Metrics] First message event received at ${timestamp}`);
  }
  if (event === 'first_typing' && !ablyMetrics.firstTypingEventMs) {
    ablyMetrics.firstTypingEventMs = timestamp;
  }
  
  // Auto-save last snapshot to localStorage for retrieval after reload
  try {
    localStorage.setItem('ably_metrics_last', JSON.stringify({
      ...ablyMetrics,
      lastUpdate: new Date().toISOString(),
    }));
  } catch (e) {
    // quota exceeded or private browsing — non-fatal
    if (process.env.NODE_ENV !== "production") console.error("[realtime] ably_metrics persist failed:", e);
  }
};

// Function to mark first message received (call from Chat.js)
export const markFirstMessageReceived = () => logAblyMetric('first_message');
export const markFirstTypingReceived = () => logAblyMetric('first_typing');

// Expose metrics and dump helper for debugging (window.ablyMetrics + window.dumpAblyMetrics)
if (typeof window !== 'undefined') {
  window.ablyMetrics = ablyMetrics;
  
  // Helper to get network info when available
  const getNetworkInfo = () => {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return { available: false };
    return {
      available: true,
      effectiveType: conn.effectiveType,
      rtt: conn.rtt,
      downlink: conn.downlink,
      saveData: conn.saveData,
    };
  };
  
  // Helper to export a JSON snapshot for copy/paste from mobile
  window.dumpAblyMetrics = () => {
    updateMetricsContext();
    const snapshot = {
      ...ablyMetrics,
      clientReadyDuration: ablyMetrics.clientReadyTime && ablyMetrics.clientStartTime 
        ? ablyMetrics.clientReadyTime - ablyMetrics.clientStartTime 
        : null,
      network: getNetworkInfo(),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      route: ablyMetrics.currentRoute,
      matchId: ablyMetrics.currentMatchId,
      // Add git SHA if available (set by build process)
      buildVersion: process.env.REACT_APP_VERSION || process.env.REACT_APP_GIT_SHA || 'dev',
    };
    const json = JSON.stringify(snapshot, null, 2);
    console.log('[Ably Metrics Snapshot]', json);
    return json;
  };
  
  // Also expose a way to retrieve the last saved snapshot (survives refresh)
  window.getLastAblyMetrics = () => {
    try {
      const saved = localStorage.getItem('ably_metrics_last');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  };
  
  // Reset first-event markers (call when changing chats)
  window.resetAblyEventMarkers = () => {
    ablyMetrics.firstMessageEventMs = null;
    ablyMetrics.firstTypingEventMs = null;
  };
}

export function getRealtimeClient() {
  if (realtimeClientPromise) return realtimeClientPromise;

  realtimeClientPromise = (async () => {
    logAblyMetric('client_start');
    
    if (!ENABLE_REALTIME) {
      console.info('[realtime] 🔇 Realtime DISABLED. Enable: localStorage.setItem("ul_enable_realtime","1")');
      return createNoopClient();
    }

    const isAdminRoute = window.location.pathname.startsWith('/admin');
    const isAdminDebugEnv = window.location.hostname.includes('admin-route-debug');
    if (isAdminRoute || isAdminDebugEnv) {
      console.info('[realtime] Admin route/env detected - using noop client (no Ably)');
      return createNoopClient();
    }

    const token = getAuthToken();
    if (!token) {
      console.warn("[realtime] No auth token available for Ably client.");
      return createNoopClient();
    }

    const baseUrl = window.location.origin;
    const ablyKey = process.env.REACT_APP_ABLY_KEY;

    if (!ablyKey && !baseUrl) {
      console.warn("[realtime] No Ably configuration, using noop client");
      return createNoopClient();
    }

    const client = new Ably.Realtime.Promise({
      authUrl: `${baseUrl}/api/realtime/auth`,
      authHeaders: { Authorization: `Bearer ${token}` },
      echoMessages: false,
    });

    client.connection.on("connected", () => {
      logAblyMetric('client_ready', { clientId: client.auth.clientId });
      console.log("[realtime] ✅ Connected to Ably as clientId:", client.auth.clientId);
      _setRealtimeStatus('connected');
      _disconnectedAt = null;
      _reconnectAttempts = 0;
    });

    client.connection.on("disconnected", (stateChange) => {
      logAblyMetric('disconnected', { reason: stateChange?.reason });
      console.info("[realtime] 📡 Disconnected from Ably:", stateChange?.reason || "normal");
      _disconnectedAt = Date.now();
      _setRealtimeStatus('disconnected');
      
      // Check for degraded state after timeout
      setTimeout(() => {
        if (_disconnectedAt && (Date.now() - _disconnectedAt > DEGRADED_TIMEOUT_MS)) {
          _setRealtimeStatus('degraded');
        }
      }, DEGRADED_TIMEOUT_MS);
    });

    client.connection.on("suspended", (stateChange) => {
      logAblyMetric('suspended', { reason: stateChange?.reason });
      console.warn("[realtime] ⏸️ Connection suspended:", stateChange?.reason || "unknown");
      _setRealtimeStatus('degraded');
    });

    client.connection.on("closed", (stateChange) => {
      logAblyMetric('closed', { reason: stateChange?.reason });
      console.info("[realtime] 🔌 Connection closed:", stateChange?.reason || "normal");
      _setRealtimeStatus('disconnected');
    });

    client.connection.on("failed", (stateChange) => {
      logAblyMetric('failed', { reason: stateChange?.reason });
      console.error("[realtime] ❌ Connection failed:", stateChange?.reason || "unknown");
      _setRealtimeStatus('degraded');
    });

    // Track reconnects (PATCH 3D: degraded detection)
    client.connection.on((stateChange) => {
      if (stateChange.previous === 'disconnected' && stateChange.current === 'connecting') {
        _reconnectAttempts++;
        if (_reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          _setRealtimeStatus('degraded');
        }
      }
      if (stateChange.previous === 'disconnected' && stateChange.current === 'connected') {
        logAblyMetric('reconnect', { from: stateChange.previous });
        _setRealtimeStatus('connected');
        _reconnectAttempts = 0;
      }
    });

    console.log("[realtime] 🚀 Ably client created (connecting in background)");
    return client;
  })().catch((err) => {
    logAblyMetric('error', { message: err?.message });
    console.error("[realtime] Failed to create Ably client", err);
    realtimeClientPromise = null;
    return createNoopClient();
  });

  return realtimeClientPromise;
}

/**
 * Safe channel getter that never throws.
 * Includes debug-gated timing metrics for channel attach.
 */
export function getSafeChannel(ably, channelName) {
  if (!ably) return null;

  const channel = ably.channels.get(channelName);
  const attachStartTime = Date.now();

  const safeHandler = (logLevel, eventName) => (stateChangeOrError) => {
    try {
      const msg =
        stateChangeOrError?.reason?.message ||
        stateChangeOrError?.reason ||
        stateChangeOrError?.message ||
        stateChangeOrError ||
        '(no reason)';

      if (logLevel === 'info') console.info(`[Ably "${channelName}"] ${eventName}:`, msg);
      else console.warn(`[Ably "${channelName}"] ${eventName}:`, msg);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("[realtime] channel log error:", e);
    }
  };

  channel.on('attached', () => {
    const duration = Date.now() - attachStartTime;
    logAblyMetric('channel_attached', { channel: channelName, duration });
    if (ABLY_DEBUG) {
      ablyMetrics.channelAttachTimes[channelName] = duration;
    }
    safeHandler('info', 'Attached')();
  });
  
  channel.on('detached', () => {
    logAblyMetric('detach', { channel: channelName });
    safeHandler('warn', 'Detached')();
  });
  
  channel.on('suspended', safeHandler('warn', 'Suspended'));
  channel.on('failed', safeHandler('warn', 'Failed'));
  channel.on('error', safeHandler('warn', 'Error'));

  return channel;
}

export async function closeRealtimeClient() {
  if (!realtimeClientPromise) return;

  try {
    const client = await realtimeClientPromise;
    if (client) {
      console.log("[realtime] 🔌 Closing Ably connection (logout/teardown)");
      client.close();
    }
  } catch (err) {
    console.error("[realtime] Error closing client:", err);
  } finally {
    realtimeClientPromise = null;
  }
}
